import { useState, useEffect } from "react";
import { getData } from "../CommonElements/Functions/PostGetFun";
import { API } from "../api/API";

// Labels store - stores API values in memory & localStorage for Web Admin

const labelsStore: Record<string, string> = {};

// Default / fallback labels matching APK & Admin settings
const defaultLabels: Record<string, string> = {
  MSL: "MSL",
  NCA: "NCA",
  POB: "POB",
  OutletOrgCode: "Outlet Org Code",
  Outlet: "Outlet",
  Credited: "Credited",
  Debited: "Debited",
  EndMyDay: "End my day",
  OVP: "OVP",
  Input: "Input",
  RECIEPT: "RECIEPT",
  ISSUE: "ISSUE",
  PTR: "PTR",
  MarkAsRead: "Mark as Read",
  Me: "Me",
  Feedback: "Feedback",
};

// Common alias map for different naming conventions
const keyAliases: Record<string, string> = {
  orgcode: "OutletOrgCode",
  outletcode: "OutletOrgCode",
  outletorgcode: "OutletOrgCode",
  pcode: "OutletOrgCode",
  endmyday: "EndMyDay",
  markasread: "MarkAsRead",
  myaccount: "Me",
  me: "Me",
  outlet: "Outlet",
  msl: "MSL",
  ptr: "PTR",
  pob: "POB",
  nca: "NCA",
  feedback: "Feedback",
  ovp: "OVP",
  input: "Input",
  credited: "Credited",
  debited: "Debited",
  reciept: "RECIEPT",
  issue: "ISSUE",
};

const LABELS_STORAGE_KEY = "@app_labels";

/**
 * Helper to match capitalization case of source string onto target string
 */
function matchCase(source: string, target: string): string {
  if (!source || !target) return target;
  // If target has spaces or is a multi-word phrase (like "My Account", "End my day", "I Agree"),
  // preserve target's casing unless source is strictly lowercase.
  if (source === source.toLowerCase()) return target.toLowerCase();
  if (source === source.toUpperCase()) {
    if (target.includes(" ") || target.length > 5) {
      return target;
    }
    return target.toUpperCase();
  }
  if (source[0] === source[0].toUpperCase()) {
    return target.charAt(0).toUpperCase() + target.slice(1);
  }
  return target;
}

/**
 * Get label value by key with dynamic case, aliases, and plural ('s') support.
 * Examples:
 * - getLabel("Me") or getLabel("ME") -> returns "My Account"
 * - getLabel("Outlet") -> returns "Customer"
 * - getLabel("Outlets") -> returns "Customers"
 * - getLabel("OutletOrgCode") or getLabel("Org Code") -> returns "PCode"
 * - getLabel("PTR") -> returns "WBR"
 * - getLabel("MarkAsRead") -> returns "I Agree"
 * - getLabel("EndMyDay") -> returns "End my day"
 */
export function getLabel(key: string): string {
  if (!key) return "";

  // Helper to normalize keys (lowercase and remove non-alphanumeric chars)
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  const normKey = norm(key);

  // 1. Direct exact match in labelsStore or defaultLabels
  if (labelsStore[key]) {
    return labelsStore[key];
  }
  if (defaultLabels[key]) {
    return defaultLabels[key];
  }

  // 2. Check canonical alias
  const canonicalKey = keyAliases[normKey];
  if (canonicalKey) {
    if (labelsStore[canonicalKey]) {
      return matchCase(key, labelsStore[canonicalKey]);
    }
    if (defaultLabels[canonicalKey]) {
      return matchCase(key, defaultLabels[canonicalKey]);
    }
  }

  // 3. Case-insensitive & normalized match in labelsStore
  const storeKeyMatch = Object.keys(labelsStore).find(
    (k) => k.toLowerCase() === key.toLowerCase() || norm(k) === normKey
  );
  if (storeKeyMatch && labelsStore[storeKeyMatch]) {
    return matchCase(key, labelsStore[storeKeyMatch]);
  }

  // Normalized match in defaultLabels
  const defaultKeyMatch = Object.keys(defaultLabels).find(
    (k) => k.toLowerCase() === key.toLowerCase() || norm(k) === normKey
  );
  if (defaultKeyMatch && defaultLabels[defaultKeyMatch]) {
    return matchCase(key, defaultLabels[defaultKeyMatch]);
  }

  // 4. Smart Plural handling for keys ending in 's' or 'S' (e.g. "Outlets", "NCAs", "customers")
  if (key.length > 1 && (key.endsWith("s") || key.endsWith("S"))) {
    const baseKey = key.slice(0, -1);
    const baseValue = getLabel(baseKey);

    if (baseValue && norm(baseValue) !== norm(baseKey)) {
      let pluralResult = baseValue;
      if (baseValue.endsWith("y") || baseValue.endsWith("Y")) {
        pluralResult = baseValue.slice(0, -1) + (baseValue.endsWith("Y") ? "IES" : "ies");
      } else if (!baseValue.toLowerCase().endsWith("s")) {
        pluralResult = baseValue + (baseValue === baseValue.toUpperCase() ? "S" : "s");
      }
      return matchCase(key, pluralResult);
    }
  }

  return key;
}

/**
 * Set a label value in memory
 */
export function setLabel(key: string, value: string): void {
  if (key && value) {
    labelsStore[key] = value;
  }
}

/**
 * Save all labels to localStorage
 */
export function saveLabelsToStorage(): void {
  try {
    localStorage.setItem(LABELS_STORAGE_KEY, JSON.stringify(labelsStore));
  } catch (error) {
    console.error("Error saving labels to storage:", error);
  }
}

/**
 * Load persisted labels from localStorage on startup
 */
export function loadPersistedLabels(): boolean {
  try {
    const stored = localStorage.getItem(LABELS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      Object.assign(labelsStore, parsed);
      return true;
    }
  } catch (error) {
    console.error("Error loading persisted labels:", error);
  }
  return false;
}

// Auto-load on module import
loadPersistedLabels();

/**
 * Clear all persisted labels (on logout)
 */
export function clearPersistedLabels(): void {
  try {
    Object.keys(labelsStore).forEach((key) => delete labelsStore[key]);
    localStorage.removeItem(LABELS_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing labels:", error);
  }
}

/**
 * Process getTranslatedLabels API response and store labels
 * Supports array of label objects, single label object, or key-value dictionary
 */
export function updateLabelsFromAPI(response: any): void {
  if (!response) return;

  const rawData = response?.data?.data ?? response?.data ?? response;

  if (rawData?.label_key && rawData?.label_value) {
    setLabel(rawData.label_key, rawData.label_value);
  } else if (rawData?.LabelKey && rawData?.LabelValue) {
    setLabel(rawData.LabelKey, rawData.LabelValue);
  }

  if (Array.isArray(rawData)) {
    rawData.forEach((item: any) => {
      if (item?.label_key && item?.label_value) {
        setLabel(item.label_key, item.label_value);
      } else if (item?.LabelKey && item?.LabelValue) {
        setLabel(item.LabelKey, item.LabelValue);
      } else if (item?.key && item?.value) {
        setLabel(item.key, item.value);
      } else if (item?.name && item?.translation) {
        setLabel(item.name, item.translation);
      }
    });
  } else if (typeof rawData === "object" && rawData !== null) {
    Object.keys(rawData).forEach((k) => {
      if (typeof rawData[k] === "string") {
        setLabel(k, rawData[k]);
      }
    });
  }

  saveLabelsToStorage();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("labelsUpdated"));
  }
}

/**
 * Fetch translated labels from API for the given (or stored) orgunit_id
 */
export async function fetchTranslatedLabels(
  sUrl?: string | null,
  token?: string | null,
  orgUnitId?: string | number | null
): Promise<any> {
  const rawServerUrl = sUrl || localStorage.getItem("serverUrl") || "https://sfa.uat.nitiapp.com/";
  const serverUrl = rawServerUrl.endsWith("/") ? rawServerUrl : `${rawServerUrl}/`;
  const resolvedToken =
    token ||
    localStorage.getItem("appToken") ||
    localStorage.getItem("apptoken") ||
    localStorage.getItem("token");

  if (!resolvedToken) return null;

  const resolvedOrgUnitId =
    orgUnitId ??
    localStorage.getItem("orgUnitId") ??
    localStorage.getItem("org_unit_id") ??
    "";

  try {
    const params: Record<string, any> = {};
    if (resolvedOrgUnitId !== "" && resolvedOrgUnitId !== undefined && resolvedOrgUnitId !== null) {
      params.orgunit_id = resolvedOrgUnitId;
    }
    const response = await getData(`${serverUrl}${API.getTranslatedLabels}`, true, params).catch((err) => {
      console.error("Error fetching getTranslatedLabels:", err);
      return null;
    });

    if (response?.status === 200 || response?.data?.statusCode === 200) {
      updateLabelsFromAPI(response);
    }
    return response;
  } catch (err) {
    console.error("Error in fetchTranslatedLabels:", err);
    return null;
  }
}

/**
 * React hook to reactively get dynamic labels in components
 */
export function useLabel(key: string): string {
  const [val, setVal] = useState<string>(() => getLabel(key));

  useEffect(() => {
    setVal(getLabel(key));
    const handleUpdate = () => {
      setVal(getLabel(key));
    };
    window.addEventListener("labelsUpdated", handleUpdate);
    return () => {
      window.removeEventListener("labelsUpdated", handleUpdate);
    };
  }, [key]);

  return val;
}

/**
 * React hook returning dynamic getLabel function that triggers re-render on labelsUpdated
 */
export function useLabels(): (key: string) => string {
  const [, setTick] = useState(0);

  useEffect(() => {
    const handleUpdate = () => {
      setTick((t) => t + 1);
    };
    window.addEventListener("labelsUpdated", handleUpdate);
    return () => {
      window.removeEventListener("labelsUpdated", handleUpdate);
    };
  }, []);

  return getLabel;
}

export const NCA = "NCA";
